import { Link } from "react-router-dom";
import { ArrowRight } from "@phosphor-icons/react";
import { motion, useReducedMotion } from "framer-motion";
import { Reveal } from "@/components/marketing/Motion";
import { ContourBackground } from "@/components/marketing/SresVisuals";
import ResourceCockpit from "@/components/marketing/ResourceCockpit";
import { easeOutSoft } from "@/lib/motion";

/**
 * <CockpitHero>
 *
 * Signature two-column marketing hero — copy on the left, a live
 * <ResourceCockpit /> panel on the right. Enterprise/corporate tone:
 * subtle motion, generous whitespace, slate-forward palette.
 */
export default function CockpitHero({
  eyebrow,
  title,
  description,
  primaryCta,
  secondaryCta,
  cockpit = {},
  testid = "cockpit-hero",
}) {
  const reduce = useReducedMotion();

  return (
    <section
      className="relative w-full pt-24 md:pt-32 pb-24 md:pb-28 overflow-hidden bg-white border-b border-slate-100"
      data-testid={testid}
    >
      {/* Very subtle grid + contour backdrop */}
      <div className="absolute inset-0 grid-lines opacity-40 pointer-events-none" aria-hidden />
      <div className="absolute -right-40 -top-32 w-[680px] h-[600px] opacity-[0.55] pointer-events-none" aria-hidden>
        <ContourBackground />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-[1.05fr,1fr] gap-16 lg:gap-24 items-center">
          {/* ── Left column: copy + CTAs ─────────────────────────── */}
          <div>
            {eyebrow ? (
              <Reveal variant="small">
                <div className="inline-flex items-center gap-2.5 text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-500">
                  <span className="w-6 h-px bg-brand-blue" />
                  {eyebrow}
                </div>
              </Reveal>
            ) : null}

            <Reveal variant="hero" delay={0.08}>
              <h1
                className="mt-8 font-heading font-black text-slate-900 text-4xl sm:text-5xl lg:text-[62px] leading-[1.05] tracking-[-0.02em]"
                style={{ fontFamily: "'Fraunces', 'Chivo', ui-serif, serif" }}
              >
                {title}
              </h1>
            </Reveal>

            {description ? (
              <Reveal delay={0.2}>
                <p className="mt-8 text-slate-600 text-[17px] leading-[1.7] max-w-xl">
                  {description}
                </p>
              </Reveal>
            ) : null}

            {(primaryCta || secondaryCta) ? (
              <Reveal delay={0.32}>
                <div className="mt-10 flex flex-wrap items-center gap-3">
                  {primaryCta ? (
                    <Link
                      to={primaryCta.to}
                      className="group inline-flex items-center gap-2.5 h-12 px-7 rounded-md bg-slate-900 hover:bg-black text-white text-[13.5px] font-semibold tracking-wide transition-colors"
                      data-testid={`${testid}-cta-primary`}
                    >
                      {primaryCta.label}
                      <ArrowRight size={15} weight="bold" className="transition-transform group-hover:translate-x-0.5" />
                    </Link>
                  ) : null}
                  {secondaryCta ? (
                    <Link
                      to={secondaryCta.to}
                      className="group inline-flex items-center gap-2.5 h-12 px-7 rounded-md border border-slate-300 text-slate-800 hover:border-slate-900 hover:text-slate-900 text-[13.5px] font-semibold tracking-wide transition-colors"
                      data-testid={`${testid}-cta-secondary`}
                    >
                      {secondaryCta.label}
                      <ArrowRight size={15} weight="bold" className="transition-transform group-hover:translate-x-0.5" />
                    </Link>
                  ) : null}
                </div>
              </Reveal>
            ) : null}
          </div>

          {/* ── Right column: cockpit panel ──────────────────────── */}
          <motion.div
            initial={reduce ? false : { opacity: 0, y: 20 }}
            animate={reduce ? false : { opacity: 1, y: 0 }}
            transition={reduce ? undefined : { duration: 0.9, ease: easeOutSoft, delay: 0.15 }}
          >
            <ResourceCockpit {...cockpit} testid={`${testid}-cockpit`} />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
